
import { useState, useCallback } from 'react';
import { format, addDays, startOfWeek, endOfWeek } from 'date-fns';
import { CalendarEvent, CalendarPermissions } from '../types';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { fromLocalISOString, toLocalISOString } from '@/utils/timezoneUtils';

export const useCalendarEvents = (
  teamId: string, 
  permissions: CalendarPermissions, 
  onEventsChanged: () => void,
  deleteShiftFn?: (shiftId: string, teamId?: string) => Promise<boolean>
) => {
  const { t } = useLanguage();
  const [showAddDialog, setShowAddDialog] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [editEvent, setEditEvent] = useState<CalendarEvent | undefined>(undefined);
  const [isClearing, setIsClearing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [shiftToDelete, setShiftToDelete] = useState<CalendarEvent | undefined>(undefined);
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteShift = deleteShiftFn;

  const handleAddEvent = useCallback(() => {
    setEditEvent(undefined);
    setShowAddDialog(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setShowAddDialog(false);
    setEditEvent(undefined);
  }, []);

  const handleDayClick = useCallback((date: Date) => {
    setSelectedDate(date);
    setEditEvent(undefined);
    setShowAddDialog(true);
  }, []);

  const handleEventAdded = useCallback(() => {
    handleDialogClose();
    onEventsChanged();
  }, [handleDialogClose, onEventsChanged]);

  const handleEventClick = useCallback((event: CalendarEvent, e: React.MouseEvent, isMultiSelectMode?: boolean, onQuickEdit?: (event: CalendarEvent) => void) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    
    // If Ctrl+Click or Alt+Click, trigger quick edit
    if (e && (e.ctrlKey || e.altKey) && onQuickEdit) {
      onQuickEdit(event);
      return;
    }
    
    setSelectedDate(fromLocalISOString(event.start_date));
    setEditEvent(event);
    setShowAddDialog(true);
  }, []);

  const handleDuplicateEvent = useCallback(async (event: CalendarEvent) => {
    try {
      const duplicatedEvent = {
        ...event,
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('team_schedules')
        .insert(duplicatedEvent)
        .select()
        .single();
        
      if (error) throw error;
      
      // Logging handled automatically by database trigger
      console.log('Shift duplicated, logging handled by database trigger');
      
      toast.success(t('eventDuplicated') || 'Event duplicated');
      onEventsChanged();
    } catch (error) {
      console.error('Error duplicating event:', error);
      toast.error(t('errorDuplicatingEvent') || 'Error duplicating event');
    }
  }, [t, onEventsChanged]);

  const handleDeleteShiftRequest = useCallback((event: CalendarEvent) => {
    setShiftToDelete(event);
    setShowDeleteDialog(true);
  }, []);

  const handleConfirmDeleteShift = useCallback(async () => {
    if (!shiftToDelete) return;
    
    try {
      setIsDeleting(true);
      const success = await deleteShift(shiftToDelete.id);
      
      if (!success) {
        throw new Error('Failed to delete shift');
      }
      
      toast.success(t('eventDeleted') || 'Event deleted');
      setShowDeleteDialog(false);
      setShiftToDelete(undefined);
      onEventsChanged();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error(t('errorDeletingEvent') || 'Error deleting event');
    } finally {
      setIsDeleting(false);
    }
  }, [shiftToDelete, deleteShift, t, onEventsChanged]);

  const handleCancelDeleteShift = useCallback(() => {
    setShowDeleteDialog(false);
    setShiftToDelete(undefined);
    setIsDeleting(false);
  }, []);

  // Keep the old function for backward compatibility but redirect to confirmation
  const handleDeleteEvent = useCallback((event: CalendarEvent) => {
    handleDeleteShiftRequest(event);
  }, [handleDeleteShiftRequest]);

  const handleClearCalendar = useCallback(async (clearType: 'week' | 'month' | 'custom', currentDate: Date, customDateRange?: { from: Date | undefined, to: Date | undefined }) => {
    if (!teamId) return 0;
    
    try {
      setIsClearing(true);
      
      let startDate: Date;
      let endDate: Date;
      
      if (clearType === 'week') {
        startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
        endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
      } else if (clearType === 'month') {
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      } else if (clearType === 'custom' && customDateRange?.from) {
        startDate = customDateRange.from;
        endDate = customDateRange.to || customDateRange.from;
      } else {
        throw new Error('Invalid date range');
      }
      
      // Format dates for the database query
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(addDays(endDate, 1), 'yyyy-MM-dd'); // Add 1 day to include end date
      
      console.log(`Clearing shifts from ${formattedStartDate} to ${formattedEndDate}`);
      
      // Delete all shifts in the date range
      const { error, count } = await supabase
        .from('team_schedules')
        .delete({ count: 'exact' })
        .eq('team_id', teamId)
        .gte('start_date', formattedStartDate)
        .lt('start_date', formattedEndDate);
        
      if (error) throw error;
      
      const deletedCount = count || 0;
      console.log(`Deleted ${deletedCount} shifts`);
      
      toast.success(
        t('shiftsDeleted') || 'Shifts deleted',
        { description: `${deletedCount} ${t('shiftsRemoved') || 'shifts were removed'}` }
      );
      
      onEventsChanged();
      return deletedCount;
    } catch (error) {
      console.error('Error clearing calendar:', error);
      toast.error(t('errorClearingCalendar') || 'Error clearing calendar');
      return 0;
    } finally {
      setIsClearing(false);
    }
  }, [teamId, t, onEventsChanged]);

  const handleDragEnd = useCallback(async (event: CalendarEvent, start: Date, end: Date) => {
    if (!permissions.canUpdate) return;
    
    try {
      const { error } = await supabase
        .from('team_schedules')
        .update({
          start_date: toLocalISOString(start),
          end_date: toLocalISOString(end),
          updated_at: new Date().toISOString()
        })
        .eq('id', event.id);
        
      if (error) throw error;
      
      toast.success(t('eventMoved') || 'Event moved');
      onEventsChanged();
    } catch (error) {
      console.error('Error moving event:', error);
      toast.error(t('errorMovingEvent') || 'Error moving event');
    }
  }, [permissions.canUpdate, t, onEventsChanged]);

  return {
    showAddDialog,
    editEvent,
    selectedDate,
    isClearing,
    showDeleteDialog,
    shiftToDelete,
    isDeleting,
    handleAddEvent,
    handleDialogClose,
    handleDayClick,
    handleEventAdded,
    handleEventClick,
    handleDuplicateEvent,
    handleDeleteEvent,
    handleDeleteShiftRequest,
    handleConfirmDeleteShift,
    handleCancelDeleteShift,
    handleClearCalendar,
    handleDragEnd
  };
};
